import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-navy-900">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/34220542/pexels-photo-34220542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
            alt="Aesthetic clinic" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/70 to-navy-900/40" />
        </div>
        <div className="relative flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-xl text-white">Dermalis CRM</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-200">Medical Aesthetics Platform</div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-display text-4xl font-bold text-white leading-tight tracking-tight">
            The complete CRM for medical aesthetics distribution.
          </h2>
          <p className="text-blue-100/80 mt-4 text-base max-w-md leading-relaxed">
            Manage clinics, pipeline, mileage, commissions and training across New Zealand & Australia — built for field teams.
          </p>
          <div className="flex gap-8 mt-10">
            {[["12+", "Modules"], ["5", "User Roles"], ["NZ & AU", "Coverage"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-2xl font-bold text-white">{n}</div>
                <div className="text-xs text-blue-200/70 uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-xl bg-navy-900 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg text-navy-900">Dermalis CRM</span>
          </div>

          <h1 className="font-display text-3xl font-bold text-navy-900 tracking-tight">Welcome back</h1>
          <p className="text-slate-500 mt-1.5 text-sm">Sign in to access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  placeholder="you@dermalis.co.nz" className="pl-9 h-11" data-testid="login-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  placeholder="••••••••" className="pl-9 h-11" data-testid="login-password" />
              </div>
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full h-11 bg-navy-900 hover:bg-navy-800 text-base" data-testid="login-submit">
              {busy ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </form>

          <p className="mt-8 text-xs text-slate-400 text-center">
            Need access? Contact your Dermalis administrator to have an account created.
          </p>
        </div>
      </div>
    </div>
  );
}
